import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Debounce hook
function useDebouncedValue(value, delay = 180) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function AutocompleteInput({ value, onChange, towary, onSelect }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const portalRef = useRef(null);

  const debouncedQ = useDebouncedValue(value, 180);

  const suggestions = useMemo(() => {
    const q = (debouncedQ || "").trim();
    if (q.length < 3) return [];
    return towary
      .filter(t => t?.active !== false)
      .filter(t => (t?.nazwa || "").toLowerCase().includes(q.toLowerCase()))
      .slice(0, 10);
  }, [debouncedQ, towary]);

  useEffect(() => {
    if (!open || !inputRef.current || !portalRef.current || suggestions.length === 0) return;

    const updatePosition = () => {
      if (!inputRef.current || !portalRef.current) return;
      
      const inputRect = inputRef.current.getBoundingClientRect();
      const portal = portalRef.current;
      
      portal.style.left = `${inputRect.left}px`;
      portal.style.top = `${inputRect.bottom + 4}px`;
      portal.style.width = `${inputRect.width}px`;
    };

    updatePosition();
    
    const handleUpdate = () => updatePosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [open, suggestions.length]);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setOpen(true);
  };

  const handleInputFocus = () => {
    if ((value || "").length >= 3) setOpen(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setOpen(false), 150);
  };

  const handleItemSelect = (item) => {
    onSelect?.(item); // Przekazujemy cały obiekt do rodzica
    setOpen(false);
  };

  const dropdownPortal = open && suggestions.length > 0 ? createPortal(
    <div ref={portalRef} className="kst-autocomplete-portal">
      <ul className="kst-autocomplete__list" role="listbox">
        {suggestions.map(item => (
          <li
            key={item.id}
            className="kst-autocomplete__item"
            role="option"
            onMouseDown={(e) => {
                e.preventDefault(); // Zapobiega blur przed wyborem
                handleItemSelect(item);
            }}
          >
            <span className="kst-autocomplete__name">{item.nazwa}</span>
          </li>
        ))}
      </ul>
    </div>,
    document.body
  ) : null;

  return (
    <div className="kst-autocomplete">
      <input
        ref={inputRef}
        className="table-input w-full rounded-md border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
        type="text"
        value={value || ""}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder="Wpisz nazwę..."
        autoComplete="off"
      />
      {dropdownPortal}
    </div>
  );
}

export default AutocompleteInput;